<template>
  <div style="position: relative; height: 30dvh; background-color: #000000;">
    <Line :data="chartData" :options="chartOptions" />
  </div>
</template>

<script>
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ScatterController
} from 'chart.js'
import { Line } from 'vue-chartjs'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ScatterController
)

export default {
  name: 'SurgeryChart',
  components: {
    Line
  },
  props: {
    logs: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    formattedLogs() {
      // 確保資料按時間排序
      return [...this.logs].sort((a, b) => a.timeSeconds - b.timeSeconds)
    },
    chartData() {
      const labels = this.formattedLogs.map(log => log.timeLabel)
      
      return {
        labels: labels,
        datasets: [
          {
            label: 'SpO2 (%)',
            backgroundColor: '#FF6384',
            borderColor: '#FF6384',
            data: this.formattedLogs.map(l => l.data?.SpO2),
            yAxisID: 'y',
            spanGaps: true,
            tension: 0
          },
          {
            label: 'EtCO2',
            backgroundColor: '#36A2EB',
            borderColor: '#36A2EB',
            data: this.formattedLogs.map(l => l.data?.EtCO2),
            yAxisID: 'y',
            spanGaps: true,
            tension: 0
          },
          {
            label: 'HR',
            backgroundColor: '#FFCE56',
            borderColor: '#FFCE56',
            data: this.formattedLogs.map(l => l.data?.HR),
            yAxisID: 'y1',
            spanGaps: true,
            tension: 0
          },
          {
            label: 'RR',
            backgroundColor: '#4BC0C0',
            borderColor: '#4BC0C0',
            data: this.formattedLogs.map(l => l.data?.RR),
            yAxisID: 'y',
            spanGaps: true,
            tension: 0
          },
           {
            label: 'BT',
            backgroundColor: '#9966FF',
            borderColor: '#9966FF',
            data: this.formattedLogs.map(l => l.data?.BT),
            yAxisID: 'y2',
             spanGaps: true,
             tension: 0
          },
          {
            label: 'SYS',
            backgroundColor: '#FF9F40',
            borderColor: '#FF9F40',
            data: this.formattedLogs.map(l => l.data?.SYS),
            yAxisID: 'y1', // 與 HR 同轴或獨立
             spanGaps: true,
             tension: 0
          },
          {
            label: 'DIA',
            backgroundColor: '#C9CBCF',
            borderColor: '#C9CBCF',
            data: this.formattedLogs.map(l => l.data?.DIA),
            yAxisID: 'y1',
             spanGaps: true,
             tension: 0
          },
          {
            label: 'MAP',
            backgroundColor: '#537bc4',
            borderColor: '#537bc4',
            data: this.formattedLogs.map(l => l.data?.MAP),
            yAxisID: 'y1',
             spanGaps: true,
             tension: 0
          },
           {
            label: 'Fluid rate',
            backgroundColor: '#acc236',
            borderColor: '#acc236',
            data: this.formattedLogs.map(l => l.data?.Fluid_rate),
            yAxisID: 'y',
            hidden: true // 預設隱藏以免太亂
          },
          {
            label: '麻醉用藥',
            type: 'scatter', 
            backgroundColor: '#ffffff',
            borderColor: '#ffffff',
            pointRadius: 6,
            pointStyle: 'rectRot', // Flag-like marker
            showLine: false,
            data: this.formattedLogs.map(l => (l.type === 'medicine' ? 5 : null)),
            yAxisID: 'y', 
            order: 0
          }
        ]
      }
    },
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        backgroundColor: '#000000', // Black background for "Monitor" look
        interaction: {
            mode: 'index',
            intersect: false,
        },
        scales: {
          x: {
              title: {
                  display: true,
                  text: '時間 (mm:ss)',
                  color: '#eee'
              },
              grid: {
                  color: '#444'
              },
              ticks: {
                  color: '#eee'
              }
          },
          y: {
            type: 'linear',
            display: true,
            position: 'left',
            title: {
                display: true,
                text: '數值',
                color: '#888'
            },
            grid: {
                  color: '#333'
            },
            ticks: {
                  color: '#888'
            }
          },
          y1: {
            type: 'linear',
            display: true,
            position: 'right',
            grid: {
              drawOnChartArea: false,
            },
            title: {
                display: true,
                text: 'HR / BP',
                color: '#888'
            },
            ticks: {
                  color: '#888'
            }
          },
           y2: {
            type: 'linear',
            display: true,
            position: 'right',
            grid: {
              drawOnChartArea: false,
            },
             min: 30,
             max: 45,
             title: {
                display: true,
                text: 'BT',
                color: '#888'
            },
            ticks: {
                  color: '#888'
            }
          },
        },
        plugins: {
            legend: {
                labels: {
                    color: '#ccc'
                }
            },
            tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                titleColor: '#fff',
                bodyColor: '#fff',
                callbacks: {
                    afterBody: (context) => {
                        const index = context[0].dataIndex;
                        const log = this.formattedLogs[index];
                        if (log && log.type === 'medicine') {
                             return `[麻醉用藥] ${log.data.item_name}: ${log.data.dosage_actual}`;
                        }
                        if (log && log.data?.Event) {
                            return `事件: ${log.data.Event}`;
                        }
                        return '';
                    },
                    label: (context) => {
                         if (context.dataset.label === '麻醉用藥') {
                             // Custom label for medicine points
                             const log = this.formattedLogs[context.dataIndex];
                             if (log.type === 'medicine') {
                                 return `${log.data.item_name} ${log.data.dosage_actual}`;
                             }
                        }
                        return context.formattedValue ? `${context.dataset.label}: ${context.formattedValue}` : '';
                    }
                }
            }
        }
      }
    }
  }
}
</script>
